import {useParams} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {Alert, Typography} from 'antd';
import {REFERRAL_PARTNER_OVERVIEW} from '../resolvers';
import MetricCard from 'components/metricCard/MetricCard';
import {currencySymbols} from 'utils/mock';
import {toast} from 'react-toastify';
import styles from './refOveriew.module.scss';

const {Text} = Typography;

export const UserReferralPartnerOverview = () => {
  const {id: partnerId} = useParams();

  const {data, loading, error} = useQuery(REFERRAL_PARTNER_OVERVIEW, {
    variables: {input: {partnerId}},
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'no-cache',
  });

  const overview =
    data?.referralPartnerGetOverview?.__typename === 'ReferralPartnerOverview'
      ? data.referralPartnerGetOverview
      : null;

  const currency = overview?.currency || 'XOF';
  const symbol = currencySymbols?.[currency]?.symbol || `${currency} `;

  const money = amount =>
    `${symbol}${Number(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const count = value => Number(value || 0).toLocaleString('en-US');

  if (!loading && (error || !overview)) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load this partner's overview"
        description={
          error?.message ||
          data?.referralPartnerGetOverview?.message ||
          'Please try again.'
        }
      />
    );
  }

  const totalReferees = Number(overview?.totalReferees || 0);
  const invested = Number(overview?.activeReferees || 0);
  const investedShare = totalReferees
    ? `${((invested / totalReferees) * 100).toFixed(0)}% of referees`
    : null;

  return (
    <div className={styles.overview}>
      <section className={styles.group}>
        <div className={styles.groupHead}>
          <h2>Investment Performance</h2>
          <Text type="secondary">
            Earnings from trading fees and new investor commission.
          </Text>
        </div>
        <div className={styles.grid}>
          <MetricCard
            label="Total Earnings"
            loading={loading}
            value={money(overview?.totalEarnings)}
            caption="Includes Academy commission"
          />
          <MetricCard
            label="Total Volume"
            loading={loading}
            value={money(overview?.totalVolume)}
          />
          <MetricCard
            label="Avg per Referee"
            loading={loading}
            value={money(overview?.avgEarningsPerReferee)}
          />
          <MetricCard
            label="Total Referees"
            loading={loading}
            value={count(overview?.totalReferees)}
          />
          <MetricCard
            label="Has Invested"
            loading={loading}
            hint="Referees with at least one settled investment. Buying a course does not count."
            value={count(overview?.activeReferees)}
            caption={investedShare}
            tone="positive"
          />
          <MetricCard
            label="Not Yet Invested"
            loading={loading}
            value={count(overview?.inactiveReferees)}
            tone="muted"
          />
        </div>
      </section>

      <section className={styles.group}>
        <div className={styles.groupHead}>
          <h2>Daba Academy</h2>
          <Text type="secondary">
            Course sales made with promo codes this partner owns. Attribution
            runs overnight, so today&apos;s sales appear tomorrow.
          </Text>
        </div>
        <div className={styles.grid}>
          <MetricCard
            label="Academy Earnings"
            loading={loading}
            value={money(overview?.totalAcademyEarnings)}
            tone="brand"
          />
          <MetricCard
            label="Academy Sales"
            loading={loading}
            value={count(overview?.totalAcademySales)}
            tone="brand"
          />
          <MetricCard
            label="Academy Volume"
            loading={loading}
            value={money(overview?.totalAcademyVolume)}
            tone="brand"
          />
        </div>
      </section>
    </div>
  );
};
